// src/components/assistant/ChatGPTPromptsTab.js
import {
    Heading, Grid, GridItem, VStack, FormControl, FormLabel, Input, Textarea,
    Box, Flex, Divider, Container,
    Slider, SliderTrack, SliderFilledTrack, SliderThumb, Text, Select,
    ListItem, List, HStack, Skeleton, IconButton, useToast, Menu, MenuButton, MenuList, MenuItem, Button, Tag, useColorMode
} from '@chakra-ui/react';
import FileSelect from './FileSelect';
import ToolSelect from './ToolSelect';
import MCPSelect from './MCPSelect';
import CopyableText from '../CopyableText';
import GenerateInstruction from '../GenerateInstruction';
import { useState } from 'react';
import VariableHelpPopover from './VariableHelpPopover';
import axios from '../../utils/axiosConfig';
import { useEffect } from 'react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useAssistant } from "../../context/AssistantContext";

const ChatGPTPromptsTab = ({ modelRef }) => {
    const {
        formState,
        handleInputChange,
        knowledgeBaseFiles,
        handleSliderChange,
        tools,
        setFormState,
        mcps
    } = useAssistant();
    const { colorMode } = useColorMode();
    const predefinedVariables = [
        { name: "fullDate", description: "31 October 2024" },
        { name: "fullDateWithoutYear", description: "31 October" },
        { name: "currentTime", description: "HH:mm:ss (e.g 13:22:59)" },
        { name: "day", description: "Numeric day of the month (1-31)" },
        { name: "month", description: "Numeric month (1-12)" },
        { name: "year", description: "Full year (e.g., 2024)" },
        { name: "dayOfWeek", description: "Full name of the day of the week (e.g Monday)" },
    ];

    const toast = useToast();

    const [generating, setGenerating] = useState(false);
    // State for models and loading
    const [models, setModels] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [instruction, setInstruction] = useState(formState.systemPrompt || "");

    useEffect(() => {
        // Reset local message when assistant or formState changes
        setInstruction(formState.systemPrompt || "");
    }, [formState.systemPrompt, formState._id]);

    const handleGenerateInstruction = (instruction) => {
        setInstruction(instruction);
        setFormState((prev) => ({
            ...prev,
            systemPrompt: instruction
        }));
    };

    const fetchAssistantModels = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get('/api/protected/assistants/models');
            if (response.data?.success && response.data?.data?.models) {
                // Ensure they’re sorted by `order` from backend; fallback to createdAt
                const rows = response.data.data.models.slice().sort((a, b) => {
                    if (a.order != null && b.order != null) return a.order - b.order;
                    return new Date(a.createdAt) - new Date(b.createdAt);
                });
                setModels(rows);
            } else {
                toast({ title: 'Failed to retrieve models', status: 'error', description: 'Invalid response format' });
            }
        } catch (error) {
            console.error('Error fetching models:', error);
            toast({
                title: 'Failed to retrieve models',
                status: 'error',
                description: error.response?.data?.message || 'An error occurred',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const [firstMessage, setFirstMessage] = useState(formState.firstMessage || "");
    const [name, setName] = useState(formState.name || "");
    const [maxTokens, setMaxTokens] = useState(formState.maxTokens || "");

    useEffect(() => {
        setFirstMessage(formState.firstMessage || "");
        setName(formState.name || "");
        setMaxTokens(formState.maxTokens || "");
    }, [formState._id, formState.firstMessage, formState.name, formState.maxTokens]);

    const handleLocalInputChange = (setter) => (e) => {
        setter(e.target.value);
    };

    const handleLocalBlur = (name, value) => {
        handleInputChange({ target: { name, value } });
    };

    const handleInstructionChange = (e) => {
        setInstruction(e.target.value);
    };

    const handleBlur = () => {
        setFormState((prev) => ({
            ...prev,
            systemPrompt: instruction
        }));
    };


    useEffect(() => {
        fetchAssistantModels();
    }, []);

    return (
        <Grid templateColumns={{
            base: 'repeat(1, 1fr)',  // 1 column on small screens (mobile)
            md: 'repeat(2, 1fr)',    // 2 columns on medium screens (tablets)
            lg: 'repeat(4, 1fr)'
        }}    // 4 columns on large screens (desktops)
            gap={6}>
            {/* Left Column */}
            <GridItem colSpan={3}>
                <VStack spacing={4} align="stretch">
                    <FormControl isRequired>
                        <FormLabel>Initial Greeting Message</FormLabel>
                        <Input
                            placeholder="Enter the first message"
                            name="firstMessage"
                            value={firstMessage}
                            onChange={handleLocalInputChange(setFirstMessage)}
                            onBlur={() => handleLocalBlur('firstMessage', firstMessage)}
                            bg={colorMode === 'light' ? 'white' : 'gray.700'}
                            color={colorMode === 'light' ? 'gray.800' : 'white'}
                            borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                            _hover={{
                                borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                            }}
                            _focus={{
                                borderColor: 'blue.500',
                                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                            }}
                        />
                    </FormControl>
                    <FormControl isRequired>
                        <HStack justify="space-between" spacing={2} width="100%">
                            {/* Left group: FormLabel + Help Popover */}
                            <HStack spacing={2}>
                                <FormLabel
                                    display="inline"
                                    color={colorMode === 'light' ? 'gray.700' : 'white'}
                                    m={0} // remove extra margin
                                >
                                    Instruction Prompt
                                </FormLabel>
                                <VariableHelpPopover predefinedVariables={predefinedVariables} />
                            </HStack>

                            {/* Right item */}
                            <GenerateInstruction
                                onGenerate={handleGenerateInstruction}
                                setGenerating={setGenerating}
                                generating={generating}
                                models={models}
                            />
                        </HStack>

                        {generating ? (
                            <Box height="500px" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                                <VStack spacing={2} align="stretch">
                                    <Skeleton height="20px" w="100%" />
                                    <Skeleton height="20px" w="95%" />
                                    <Skeleton height="20px" w="90%" />
                                    <Skeleton height="20px" w="85%" />
                                    <Skeleton height="20px" w="80%" />
                                    <Skeleton height="20px" w="20%" />
                                    <Skeleton height="20px" w="40%" />
                                    <Skeleton height="20px" w="100%" />
                                    <Skeleton height="20px" w="95%" />
                                    <Skeleton height="20px" w="90%" />
                                    <Skeleton height="20px" w="85%" />
                                    <Skeleton height="20px" w="80%" />
                                    <Skeleton height="20px" w="20%" />
                                    <Skeleton height="20px" w="40%" />
                                </VStack>
                            </Box>
                        ) : (
                            <Textarea
                                placeholder="Enter the system prompt"
                                name="systemPrompt"
                                value={instruction || ''}
                                onChange={handleInstructionChange}
                                onBlur={handleBlur}
                                bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                color={colorMode === 'light' ? 'gray.800' : 'white'}
                                borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                _hover={{
                                    borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                                }}
                                _focus={{
                                    borderColor: 'blue.500',
                                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                }}
                                height="500px"
                            />
                        )}
                    </FormControl>
                </VStack>
            </GridItem>

            {/* Right Column */}
            <GridItem>
                <VStack spacing={4} align="stretch">
                    <FormControl isRequired>
                        <VStack
                            spacing={4}
                            align="stretch"
                        >
                            <Text>Assistant ID:</Text> <CopyableText text={formState._id} colorMode={colorMode}></CopyableText>
                        </VStack>
                        <FormLabel color={colorMode === 'light' ? 'gray.700' : 'white'}>Assistant Name</FormLabel>
                        <Input
                            placeholder="Enter assistant name"
                            name="name"
                            value={name}
                            onChange={handleLocalInputChange(setName)}
                            onBlur={() => handleLocalBlur('name', name)}
                            bg={colorMode === 'light' ? 'white' : 'gray.700'}
                            color={colorMode === 'light' ? 'gray.800' : 'white'}
                            borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                            _hover={{
                                borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                            }}
                            _focus={{
                                borderColor: 'blue.500',
                                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                            }}
                        />
                    </FormControl>
                    <Flex direction="column">
                        <Flex alignItems="center">
                            <Text mr={2} color={colorMode === 'light' ? 'gray.700' : 'white'}>Temperature:</Text>
                            <Text color="brand.400">{formState.temperature || 0.6}</Text>
                        </Flex>
                        <Slider
                            aria-label="temperature-slider"
                            value={formState.temperature || 0.5}
                            onChange={(val) => handleSliderChange(val, 'temperature')}
                            min={0}
                            max={1}
                            step={0.1}
                        >
                            <SliderTrack bg={colorMode === 'light' ? 'gray.200' : 'gray.600'}>
                                <SliderFilledTrack bg="blue.400" />
                            </SliderTrack>
                            <SliderThumb />
                        </Slider>
                        <Text fontSize="sm" mr={2} color={colorMode === 'light' ? 'gray.600' : 'gray.400'}>Note: If you are using RealTime, set this to 0.6 and above</Text>
                    </Flex>

                    <FormControl isRequired>
                        <FormLabel color={colorMode === 'light' ? 'gray.700' : 'white'}>Max Output Token</FormLabel>
                        <Input
                            placeholder="Enter max tokens"
                            name="maxTokens"
                            type="number"
                            value={maxTokens}
                            onChange={handleLocalInputChange(setMaxTokens)}
                            onBlur={() => handleLocalBlur('maxTokens', maxTokens)}
                            bg={colorMode === 'light' ? 'white' : 'gray.700'}
                            color={colorMode === 'light' ? 'gray.800' : 'white'}
                            borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                            _hover={{
                                borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                            }}
                            _focus={{
                                borderColor: 'blue.500',
                                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                            }}
                        />
                    </FormControl>

                    <FormControl isRequired>
                        <FormLabel color={colorMode === 'light' ? 'gray.700' : 'white'}>LLM Models</FormLabel>
                        {/* <Select
                            ref={modelRef}
                            name="model"
                            bg={colorMode === 'light' ? 'white' : 'gray.700'}
                            color={colorMode === 'light' ? 'gray.800' : 'white'}
                            borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                            _hover={{
                                borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                            }}
                            value={formState.model}
                            onChange={handleInputChange}
                            required={true}
                        >
                            {models.map((m) => {
                                // Build the select value. If backend has m.kind (e.g. 'complete', 'realtime'), include it.
                                const optionValue = m.value;

                                return (
                                    <option key={m._id} value={optionValue}>
                                        {m.name}
                                    </option>
                                );
                            })}
                        </Select> */}
                        <Menu>
                            <MenuButton
                                as={Button}
                                rightIcon={<ChevronDownIcon />}
                                bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                color={colorMode === 'light' ? 'gray.800' : 'white'}
                                // borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                borderColor="black"
                                _hover={{ borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500' }}
                                w="full"
                            >
                                {formState.model
                                    ? models.find((m) => m.value === formState.model)?.name
                                    : 'Select a model'}
                            </MenuButton>
                            <MenuList>
                                {models.map((m) => (
                                    <MenuItem
                                        key={m._id}
                                        onClick={() =>
                                            handleInputChange({
                                                target: { name: 'model', value: m.value },
                                            })
                                        }
                                    >
                                        <Flex justify="space-between" align="center" w="full">
                                            <Box>{m.name}</Box>
                                            {m.inputType && m.outputType && (() => {
                                                let colorScheme = "gray"; // default

                                                const pair = `${m.inputType}:${m.outputType}`;
                                                switch (pair) {
                                                    case "text:text":
                                                        colorScheme = "teal";
                                                        break;
                                                    case "text:audio":
                                                        colorScheme = "purple";
                                                        break;
                                                    case "audio:audio":
                                                        colorScheme = "orange";
                                                        break;
                                                    default:
                                                        colorScheme = "gray";
                                                }

                                                return (
                                                    <Tag size="sm" colorScheme={colorScheme} ml={2}>
                                                        {m.inputType} → {m.outputType}
                                                    </Tag>
                                                );
                                            })()}
                                        </Flex>
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </Menu>
                    </FormControl>


                </VStack>
            </GridItem>
            <GridItem colSpan={4}>
                <Box mt={6} p={4} borderWidth="1px" borderRadius="md" boxShadow="lg" bg={colorMode === 'light' ? 'white' : 'gray.700'} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}>
                    <Heading size="xs" mb={4} color={colorMode === 'light' ? 'gray.800' : 'white'}>Attach Knowledge Base Files</Heading>
                    <FileSelect
                    // formState={formState}
                    // knowledgeBaseFiles={knowledgeBaseFiles}
                    // setFormState={setFormState}
                    />
                </Box>


                <Box mt={6} p={6} borderWidth="1px" borderRadius="md" boxShadow="lg" bg={colorMode === 'light' ? 'white' : 'gray.700'} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}>
                    <Heading size="xs" mb={4} color={colorMode === 'light' ? 'gray.800' : 'white'}>Attach Tools</Heading>
                    <Divider mb={4} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} />
                    <ToolSelect
                    // formState={formState}
                    // tools={tools}
                    // setFormState={setFormState}
                    />
                </Box>

                <Box mt={6} p={6} borderWidth="1px" borderRadius="md" boxShadow="lg" bg={colorMode === 'light' ? 'white' : 'gray.700'} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}>
                    <Heading size="xs" mb={4} color={colorMode === 'light' ? 'gray.800' : 'white'}>Attach MCPs</Heading>
                    <Divider mb={4} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} />
                    <MCPSelect
                    // formState={formState}
                    // mcps={mcps}
                    // setFormState={setFormState}
                    />
                </Box>
            </GridItem>
        </Grid>
    )
};

export default ChatGPTPromptsTab;